import React, { useState } from 'react';
import { Ticket, Search, LogOut, Bell } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

// --- SIMULACIÓN DE BASE DE DATOS CON TICKETS ÚNICOS ---
const mockDB = {
    orders: [
        { id: '12345', customer: 'Carlos Sánchez' },
        { id: '67890', customer: 'Ana Torres' }
    ],
    tickets: [
        // Tickets para la orden 12345
        { id: 'tkt-a1b2', orderId: '12345', name: 'Taco de Pastor', status: 'activo' },
        { id: 'tkt-c3d4', orderId: '12345', name: 'Taco de Pastor', status: 'activo' },
        { id: 'tkt-e5f6', orderId: '12345', name: 'Refresco', status: 'activo' },
        // Tickets para la orden 67890
        { id: 'tkt-g7h8', orderId: '67890', name: 'Gordita de Chicharrón', status: 'canjeado' },
        { id: 'tkt-i9j0', orderId: '67890', name: 'Agua de Horchata', status: 'activo' },
        { id: 'tkt-k1l2', orderId: '67890', name: 'Elote', status: 'activo' }
    ]
};

const HostDashboard = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [orderIdInput, setOrderIdInput] = useState('');
    const [currentOrder, setCurrentOrder] = useState(null);
    const [currentTickets, setCurrentTickets] = useState([]);
    const [feedback, setFeedback] = useState({ message: '', type: '' });

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setFeedback({ message: '', type: '' });
        const order = mockDB.orders.find(o => o.id === orderIdInput);
        if (order) {
            const ticketsForOrder = mockDB.tickets.filter(t => t.orderId === order.id);
            setCurrentOrder(order);
            setCurrentTickets(ticketsForOrder);
        } else {
            setCurrentOrder(null);
            setCurrentTickets([]);
            setFeedback({ message: `No se encontró la orden con el folio "${orderIdInput}".`, type: 'error' });
        }
    };

    const handleRedeem = (ticketId) => {
        // En una app real, esto sería una llamada a la API.
        // Aquí, actualizamos el estado local.
        const ticket = mockDB.tickets.find(t => t.id === ticketId); // Busca en la "DB" principal
        if (ticket && ticket.status === 'activo') {
            ticket.status = 'canjeado';
            // Forzamos una re-renderización actualizando el estado de los tickets
            setCurrentTickets([...currentTickets]);
            setFeedback({ message: `Ticket "${ticket.name}" canjeado exitosamente.`, type: 'success' });
        }
    };

    return (
        <>
            <aside className="w-64 bg-white border-r border-gray-200 hidden md:flex flex-col">
                <div className="p-6 flex items-center border-b border-gray-100">
                    <Ticket className="text-blue-600 mr-2" />
                    <span className="text-xl font-bold text-gray-800">Kermes<span className="text-blue-600">Host</span></span>
                </div>
                <nav className="flex-1 p-4">
                    <ul className="space-y-2">
                        <li className="flex items-center p-3 rounded-lg bg-blue-50 text-blue-600 cursor-pointer">
                            <Ticket size={20} className="mr-3" />
                            <span className="font-medium">Canjear Tickets</span>
                        </li>
                    </ul>
                </nav>
                <div className="p-4 border-t border-gray-100">
                    <button onClick={handleLogout} className="flex items-center text-gray-600 hover:text-red-600 transition-colors w-full p-2">
                        <LogOut size={20} className="mr-3" />
                        <span>Cerrar Sesión</span>
                    </button>
                </div>
            </aside>

            <main className="flex-1 flex flex-col overflow-hidden">
                <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-8">
                    <h1 className="text-xl font-bold text-gray-800">Canje de Tickets de Orden</h1>
                    <div className="flex items-center space-x-4">
                        <button className="relative p-2 text-gray-400 hover:text-gray-600"><Bell size={20} /></button>
                        <div className="flex items-center space-x-2">
                            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold"><Ticket size={18} /></div>
                            <span className="text-sm font-medium text-gray-700">{user.name} | Sitio: {user.clinicId}</span>
                        </div>
                    </div>
                </header>

                <div className="flex-1 overflow-y-auto p-8">
                    <div className="max-w-2xl mx-auto">
                        <form onSubmit={handleSearch} className="bg-white p-6 rounded-xl shadow-md border border-gray-100 mb-8">
                            <label htmlFor="order-id-input" className="block text-sm font-medium text-gray-700 mb-1">Folio de la Orden</label>
                            <div className="flex space-x-2">
                                <input
                                    type="text"
                                    id="order-id-input"
                                    value={orderIdInput}
                                    onChange={(e) => setOrderIdInput(e.target.value)}
                                    placeholder="Ej: 12345"
                                    required
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <button type="submit" className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center">
                                    <Search size={18} className="mr-2" /> Buscar
                                </button>
                            </div>
                        </form>

                        {feedback.message && (
                            <div className={`p-4 mb-4 rounded-lg ${feedback.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                                {feedback.message}
                            </div>
                        )}

                        {currentOrder && (
                            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                                <div className="border-b pb-4 mb-4">
                                    <h3 className="text-lg font-bold">Orden #{currentOrder.id}</h3>
                                    <p className="text-sm text-gray-600">Cliente: {currentOrder.customer}</p>
                                </div>
                                <ul className="space-y-3">
                                    {currentTickets.map(ticket => (
                                        <li key={ticket.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                                            <span>
                                                <strong>{ticket.name}</strong>
                                                <small className="block text-gray-500">ID: {ticket.id}</small>
                                            </span>
                                            <button
                                                onClick={() => handleRedeem(ticket.id)}
                                                disabled={ticket.status === 'canjeado'}
                                                className={`px-4 py-1.5 text-sm font-semibold rounded-full transition-colors ${ticket.status === 'canjeado' ? 'bg-green-500 text-white cursor-not-allowed' : 'bg-blue-500 text-white hover:bg-blue-600'}`}
                                            >
                                                {ticket.status === 'canjeado' ? 'Canjeado' : 'Canjear'}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </>
    );
};

export default HostDashboard;